import React, { useState, useEffect } from 'react';
import { BookOpen, Sparkles, Send, Flame, Heart, TrendingUp, ChevronDown, ChevronUp, Lightbulb } from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { apiClient } from '../api/client';

const MOOD_LABELS = ['', 'Low', 'Down', 'Neutral', 'Good', 'Great'];
const MOOD_COLORS = ['', '#EF4444', '#F59E0B', '#6B7280', '#3B82F6', '#10B981'];

const GrowthJournal = () => {
  const [journal, setJournal] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [moodBefore, setMoodBefore] = useState(3);
  const [moodAfter, setMoodAfter] = useState(3);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showInsights, setShowInsights] = useState(false);
  const [expandedEntry, setExpandedEntry] = useState(null);

  useEffect(() => {
    Promise.all([
      apiClient.request('/api/self-dev/journal'),
      apiClient.request('/api/self-dev/journal/insights').catch(() => null),
    ]).then(([j, i]) => {
      setJournal(j);
      if (i) setInsights(i);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSubmit = async () => {
    if (content.trim().length < 10) return;
    setSubmitting(true);
    try {
      const res = await apiClient.request('/api/self-dev/journal', {
        method: 'POST',
        body: JSON.stringify({ content: content.trim(), mood_before: moodBefore, mood_after: moodAfter }),
      });
      setResult(res);
      setJournal(prev => prev ? { ...prev, this_week_written: true, entries: [res.entry, ...prev.entries] } : prev);
      setContent('');
    } catch {}
    setSubmitting(false);
  };

  if (loading) return <DashboardLayout><div className="p-8 text-center text-gray-400">Loading journal...</div></DashboardLayout>;
  if (!journal) return <DashboardLayout><div className="p-8 text-center text-gray-400">Could not load journal</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-3xl mx-auto overflow-hidden" data-testid="growth-journal-page">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-black text-gray-900 flex items-center justify-center gap-2">
            <BookOpen size={28} className="text-emerald-600" />
            Growth Journal
          </h1>
          <p className="text-sm text-gray-500 mt-1">Weekly reflections that build real self-awareness</p>
          <div className="flex items-center justify-center gap-4 mt-3 text-xs">
            <span className="flex items-center gap-1"><Flame size={12} className="text-orange-500" /> {journal.journal_streak}-week streak</span>
            <span className="text-gray-300">|</span>
            <span className="flex items-center gap-1"><BookOpen size={12} className="text-emerald-500" /> {journal.total_entries} entries</span>
          </div>
        </div>

        {/* This Week's Prompt */}
        <div className="mb-6">
          <div className="rounded-2xl overflow-hidden shadow-sm border border-emerald-200" data-testid="journal-prompt-card">
            <div className="p-5 sm:p-6 bg-gradient-to-br from-emerald-50 to-teal-50 overflow-hidden">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Sparkles size={16} className="text-emerald-600 flex-shrink-0" />
                <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-600">This Week&apos;s Prompt</span>
                <span
                  className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white"
                  style={{ backgroundColor: journal.prompt_category_info?.color || '#6B7280' }}
                >
                  {journal.prompt_category_info?.label || journal.prompt_category}
                </span>
              </div>
              <p className="text-lg sm:text-xl font-bold text-gray-900 leading-snug">{journal.prompt}</p>
            </div>

            {/* Writing Area */}
            {journal.this_week_written ? (
              <div className="p-4 bg-green-50 border-t border-green-200">
                <p className="text-sm text-green-700 font-semibold flex items-center gap-1.5">
                  <Heart size={14} /> You&apos;ve written this week&apos;s entry! Come back next week.
                </p>
                {result && <p className="text-xs text-green-600 mt-1">{result.message}</p>}
              </div>
            ) : (
              <div className="p-4 sm:p-5 bg-white border-t border-gray-100">
                {/* Mood Before */}
                <div className="mb-3">
                  <p className="text-xs font-medium text-gray-500 mb-1.5">How do you feel right now?</p>
                  <div className="flex gap-1.5">
                    {[1, 2, 3, 4, 5].map(m => (
                      <button
                        key={m}
                        onClick={() => setMoodBefore(m)}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          moodBefore === m ? 'text-white shadow-sm scale-105' : 'bg-gray-100 text-gray-500'
                        }`}
                        style={moodBefore === m ? { backgroundColor: MOOD_COLORS[m] } : {}}
                        data-testid={`mood-before-${m}`}
                      >
                        {MOOD_LABELS[m]}
                      </button>
                    ))}
                  </div>
                </div>

                <textarea
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  placeholder="Take your time. Write freely..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  rows={5}
                  data-testid="journal-content-input"
                />
                <p className="text-[10px] text-gray-400 mt-1 text-right">{content.length} characters (min 10)</p>

                {/* Mood After */}
                <div className="mt-3 mb-3">
                  <p className="text-xs font-medium text-gray-500 mb-1.5">How do you feel after writing?</p>
                  <div className="flex gap-1.5">
                    {[1, 2, 3, 4, 5].map(m => (
                      <button
                        key={m}
                        onClick={() => setMoodAfter(m)}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                          moodAfter === m ? 'text-white shadow-sm scale-105' : 'bg-gray-100 text-gray-500'
                        }`}
                        style={moodAfter === m ? { backgroundColor: MOOD_COLORS[m] } : {}}
                        data-testid={`mood-after-${m}`}
                      >
                        {MOOD_LABELS[m]}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={submitting || content.trim().length < 10}
                  className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-xl text-sm hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="submit-journal-btn"
                >
                  <Send size={14} />
                  {submitting ? 'Saving...' : 'Save Entry (+15 hearts)'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Insights */}
        {insights && insights.insights.length > 0 && (
          <div className="mb-6">
            <button
              onClick={() => setShowInsights(!showInsights)}
              className="w-full flex items-center justify-between p-4 bg-violet-50 border border-violet-200 rounded-xl hover:bg-violet-100 transition-all"
              data-testid="toggle-insights-btn"
            >
              <span className="flex items-center gap-2 text-sm font-bold text-violet-700">
                <Lightbulb size={16} /> Journal Insights
              </span>
              {showInsights ? <ChevronUp size={16} className="text-violet-500" /> : <ChevronDown size={16} className="text-violet-500" />}
            </button>
            {showInsights && (
              <div className="mt-2 space-y-2" data-testid="insights-panel">
                {insights.insights.map((insight, i) => (
                  <div key={i} className="p-3 bg-white border border-gray-200 rounded-xl">
                    <p className="text-sm font-semibold text-gray-900">{insight.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{insight.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Past Entries */}
        {journal.entries.length > 0 && (
          <div>
            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">Past Entries</h2>
            <div className="space-y-2">
              {journal.entries.map((entry, i) => (
                <div key={entry.entry_id || i} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`journal-entry-${i}`}>
                  <button
                    onClick={() => setExpandedEntry(expandedEntry === i ? null : i)}
                    className="w-full p-3 sm:p-4 flex items-center justify-between text-left hover:bg-gray-50 transition-all"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">{entry.prompt}</p>
                      <div className="flex items-center gap-2 mt-0.5 text-[10px] text-gray-400">
                        <span>{entry.week}</span>
                        <span
                          className="px-1.5 py-0.5 rounded text-white font-bold"
                          style={{ backgroundColor: MOOD_COLORS[entry.mood_after || 3] }}
                        >
                          {MOOD_LABELS[entry.mood_after || 3]}
                        </span>
                      </div>
                    </div>
                    {expandedEntry === i ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                  </button>
                  {expandedEntry === i && (
                    <div className="px-3 sm:px-4 pb-3 sm:pb-4 border-t border-gray-100 pt-3">
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{entry.content}</p>
                      <div className="flex flex-wrap gap-3 mt-3 text-[10px] text-gray-400">
                        <span>Mood before: <strong style={{ color: MOOD_COLORS[entry.mood_before || 3] }}>{MOOD_LABELS[entry.mood_before || 3]}</strong></span>
                        <span>Mood after: <strong style={{ color: MOOD_COLORS[entry.mood_after || 3] }}>{MOOD_LABELS[entry.mood_after || 3]}</strong></span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {journal.entries.length === 0 && (
          <div className="text-center py-12" data-testid="journal-empty">
            <BookOpen size={40} className="text-emerald-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Your journal is empty. Write your first entry above!</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default GrowthJournal;
